import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { toStripeRedirectUrl } from '../utils/stripe-redirect-utils';
import { CreateCheckoutParams } from '../request-models/create-checkout-params.model';
import { FinalizedPurchase } from '../response-models/finalized-purchase.model';
import { MapAnalysisCheckoutSession } from '../response-models/map-analysis-checkout-session.model';

/**
 * Map Analysis Checkout
 *
 * Calls the Hub's hosted Stripe Checkout endpoint with `acres + fieldId`.
 * The Hub computes `quantity = max(1, ceil(acres))` and creates a Checkout
 * Session against the per-acre Stripe Price — the dollar amount is LOCKED
 * at session-creation time on the server. The client never has to trust
 * its own math.
 *
 * Fresh-session rule (billing-payments § 5.1.3 / § 9): every CTA tap mints
 * a NEW session. We never reuse a stale URL, even on the same field —
 * Stripe pricing or acreage may have changed between attempts.
 *
 * Per billing-payments § 1.0 (Q0): the web NEVER imports `@stripe/stripe-js`.
 * All Stripe interaction is via hosted-page redirect.
 *
 * Failures reject with an `ApiError` (`message` is the hub's error).
 */
@Injectable({ providedIn: 'root' })
export class MapAnalysisCheckoutService extends ApiService {
  constructor() {
    super('payments/map-analysis');
  }

  async createCheckoutSession(params: CreateCheckoutParams): Promise<MapAnalysisCheckoutSession> {
    const session = await this.post<MapAnalysisCheckoutSession>('checkout-session', {
      acres: params.acres,
      fieldId: params.fieldId,
      productKey: params.productKey || 'map_analysis_per_acre',
      ...(params.returnPath ? { returnPath: params.returnPath } : {}),
    });
    if (!session?.url) {
      throw new Error('Failed to create checkout session');
    }
    return session;
  }

  /**
   * Redirect the browser to Stripe Checkout. The user leaves the SPA and
   * returns via the success_url (set server-side from STRIPE_RETURN_BASE_URL_WEB).
   * Throws when the hub's `url` isn't an https Stripe URL (`toStripeRedirectUrl`).
   */
  redirectToCheckout(session: MapAnalysisCheckoutSession): void {
    window.location.assign(toStripeRedirectUrl(session.url));
  }

  /**
   * Called by the return route after Stripe redirects back. Foreground-
   * syncs the purchase record from Stripe so the subsequent createAnalysis
   * call has a confirmed audit row to link against — even if the webhook
   * is delayed. Idempotent server-side: if the webhook already landed,
   * this is a no-op. See billing-payments § 9.4.
   *
   * Returns the persisted purchase row.
   */
  async finalizePurchase(sessionId: string): Promise<FinalizedPurchase> {
    const purchase = await this.post<FinalizedPurchase>('purchase/from-checkout-session', { sessionId });
    if (!purchase) {
      throw new Error('Failed to finalize purchase');
    }
    return purchase;
  }

  /** POST `/payments/map-analysis/{path}` and unwrap the hub's `data`. */
  private post<R>(path: string, body: unknown): Promise<R | null> {
    return firstValueFrom(this.request('POST', { path }, data => (data ?? null) as R | null, body));
  }
}
